import React, { useState, useMemo, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useData } from "@/lib/dataStore";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { formatSEK } from "@/lib/utils-app";
import { exportToExcel, buildLedgerWorkbook, blobToBase64, exportQuarterlyFilingPack, quarterRange } from "@/lib/exportXlsx";
import {
  Plus, MagnifyingGlass, Trash, ShareNetwork, Receipt as ReceiptIcon, Funnel, Warning,
  Package, CloudArrowUp, AppleLogo, CaretDown, ChartLineUp, Calendar, CurrencyCircleDollar, Archive
} from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const GRASS = ["Wheat", "Barley", "Corn"];
const CUSTOMER_TYPE = ["Human Food", "Animal Feed/Other"];
const EXPENSE_CATEGORIES = ["Grains/Seeds", "Electricity", "Water", "Equipment", "Other"];
const LB_TO_KG = 0.453592;

const todayISO = () => new Date().toISOString().slice(0, 10);

const initialExpense = () => ({
  date: todayISO(),
  category: "Grains/Seeds",
  amount_sek: "",
  vat_rate: "0.25",
  notes: "",
  supplier_name: "",
  batch_number: "",
  grain_variety: "Barley",
  quantity_kg: "",
  price_per_kg: "",
});

export default function LogsPage() {
  const { sales, expenses, receipts, settings, refresh, refreshSettings } = useData();
  const { user } = useAuth();
  const isMaster = !!user?.is_master;
  const isPreProduction = !settings?.production_started_at;
  const resetInventoryToZero = async () => {
    if (!window.confirm("Reset all grain stocks to 0 kg?\n\nThis affects Wheat, Barley, and Corn. Pre-production utility only.")) return;
    try {
      await api.post("/admin/reset-grain-stock");
      await refreshSettings();
      toast.success("Inventory reset to 0 kg across all varieties");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reset inventory");
    }
  };
  const [expenseForm, setExpenseForm] = useState(initialExpense());
  const [savingExpense, setSavingExpense] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [backingUp, setBackingUp] = useState(null);

  // Track which 2 of {amount_sek, quantity_kg, price_per_kg} the user has most recently
  // typed in. The third one becomes the auto-calculated field.
  // Most-recent-first queue of field names (max length 3).
  const editHistoryRef = useRef([]);

  const markEdited = (field) => {
    editHistoryRef.current = [field, ...editHistoryRef.current.filter((f) => f !== field)].slice(0, 3);
  };

  const handleExpenseField = (field, value) => {
    if (field === "amount_sek" || field === "quantity_kg" || field === "price_per_kg") {
      markEdited(field);
    }
    setExpenseForm((f) => ({ ...f, [field]: value }));
  };

  // Bidirectional auto-calc for Grain purchases.
  // The field NOT among the 2 most-recently user-edited is recalculated.
  const autoField = useMemo(() => {
    if (expenseForm.category !== "Grains/Seeds") return null;
    const recent = new Set(editHistoryRef.current.slice(0, 2));
    if (recent.size < 2) return null;
    return ["amount_sek", "quantity_kg", "price_per_kg"].find((f) => !recent.has(f)) || null;
  }, [
    expenseForm.category,
    expenseForm.amount_sek, expenseForm.quantity_kg, expenseForm.price_per_kg,
  ]);

  useEffect(() => {
    if (expenseForm.category !== "Grains/Seeds") return;
    const amount = parseFloat(expenseForm.amount_sek);
    const kg = parseFloat(expenseForm.quantity_kg);
    const pkg = parseFloat(expenseForm.price_per_kg);
    if (!autoField) return;

    const setIfChanged = (field, computed) => {
      const str = String(computed);
      if (str !== expenseForm[field]) {
        setExpenseForm((f) => ({ ...f, [field]: str }));
      }
    };
    if (autoField === "amount_sek" && kg > 0 && pkg > 0) {
      setIfChanged("amount_sek", +(kg * pkg).toFixed(2));
    } else if (autoField === "price_per_kg" && amount > 0 && kg > 0) {
      setIfChanged("price_per_kg", +(amount / kg).toFixed(2));
    } else if (autoField === "quantity_kg" && amount > 0 && pkg > 0) {
      setIfChanged("quantity_kg", +(amount / pkg).toFixed(2));
    }
  }, [autoField, expenseForm]);

  // History filters
  const [search, setSearch] = useState("");
  const [historyTab, setHistoryTab] = useState("sales");
  const [customerTypeFilter, setCustomerTypeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const submitExpense = async (e) => {
    e.preventDefault();
    setSavingExpense(true);
    try {
      const isGrain = expenseForm.category === "Grains/Seeds";
      const payload = {
        date: expenseForm.date,
        category: expenseForm.category,
        amount_sek: parseFloat(expenseForm.amount_sek) || 0,
        vat_rate: parseFloat(expenseForm.vat_rate || "0.25"),
        notes: expenseForm.notes || "",
        supplier_name: isGrain ? expenseForm.supplier_name || null : null,
        batch_number: isGrain ? expenseForm.batch_number || null : null,
        grain_variety: isGrain ? expenseForm.grain_variety : null,
        quantity_kg: isGrain && expenseForm.quantity_kg ? parseFloat(expenseForm.quantity_kg) : null,
        price_per_kg: isGrain && expenseForm.price_per_kg ? parseFloat(expenseForm.price_per_kg) : null,
      };
      await api.post("/expenses", payload);
      toast.success("Expense logged");
      setExpenseForm(initialExpense());
      editHistoryRef.current = [];
      refresh();
      refreshSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save expense");
    } finally {
      setSavingExpense(false);
    }
  };

  const removeSale = async (id) => {
    if (!window.confirm("Delete this sale?")) return;
    await api.delete(`/sales/${id}`);
    refresh();
    refreshSettings();
  };
  const removeExpense = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await api.delete(`/expenses/${id}`);
    refresh();
    refreshSettings();
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const result = await exportToExcel(sales, expenses, settings);
      if (result.shared) toast.success("Shared via system");
      else if (result.downloaded) toast.success("Excel file downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  // ---- Quarterly Moms Filing Pack (Skatteverket-ready zip) ----
  const currentQuarter = () => {
    const d = new Date();
    return { year: d.getUTCFullYear(), quarter: Math.floor(d.getUTCMonth() / 3) + 1 };
  };
  const [packYear, setPackYear] = useState(() => currentQuarter().year);
  const [packQuarter, setPackQuarter] = useState(() => currentQuarter().quarter);
  const [packBuilding, setPackBuilding] = useState(false);
  const [packStatus, setPackStatus] = useState("");

  const yearOptions = useMemo(() => {
    const now = new Date().getUTCFullYear();
    const yrs = new Set([now, now - 1, now - 2]);
    sales.forEach((s) => { const y = +(s.date || "").slice(0, 4); if (y) yrs.add(y); });
    receipts.forEach((r) => { const y = +(r.date || "").slice(0, 4); if (y) yrs.add(y); });
    return Array.from(yrs).sort((a, b) => b - a);
  }, [sales, receipts]);

  const buildQuarterlyPack = async () => {
    setPackBuilding(true);
    setPackStatus("Preparing…");
    try {
      const [startISO, endISO] = quarterRange(packYear, packQuarter);
      const result = await exportQuarterlyFilingPack({
        year: packYear,
        quarter: packQuarter,
        sales, expenses, receipts, settings,
        fetchReceiptPdf: async (id) => (await api.get(`/receipts/${id}/pdf`, { responseType: "blob" })).data,
        fetchCreditNotePdf: async (id) => (await api.get(`/receipts/${id}/credit-note/pdf`, { responseType: "blob" })).data,
        fetchSie4: async (yr) => (await api.get(`/ledger/sie4?year=${yr}`, { responseType: "blob" })).data,
        onProgress: (label, pct) => setPackStatus(`${label} (${Math.round(pct)}%)`),
      });
      if (result.shared) toast.success(`Filing pack ${packYear} Q${packQuarter} shared`);
      else if (result.downloaded) toast.success(`Filing pack ${packYear} Q${packQuarter} downloaded · ${startISO} → ${endISO}`);
    } catch (e) {
      toast.error(e?.message || "Quarterly pack failed");
    } finally {
      setPackBuilding(false);
      setPackStatus("");
    }
  };

  const backupToICloud = async () => {
    setBackingUp("icloud");
    try {
      const { blob, filename } = buildLedgerWorkbook(sales, expenses, settings);
      const file = new File([blob], filename, { type: blob.type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "The NordHim Ledger iCloud Backup",
          text: "Tap Save to Files → iCloud Drive",
        });
        toast.success("Saved — pick 'Save to Files' → iCloud Drive");
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.info("Downloaded — drop into your iCloud Drive folder");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("iCloud backup failed");
    } finally {
      setBackingUp(null);
    }
  };

  const backupToCloud = async (target) => {
    setBackingUp(target);
    try {
      const { blob, filename } = buildLedgerWorkbook(sales, expenses, settings);
      const content_base64 = await blobToBase64(blob);
      const path = target === "scaleway" ? "/storage/scaleway/upload" : "/storage/nas/upload";
      const { data } = await api.post(path, { filename, content_base64, content_type: blob.type });
      if (target === "scaleway") toast.success(`Backed up to Scaleway: ${data.bucket}/${data.key}`);
      else toast.success(`Backed up to ${data.label || "NAS"}: ${data.bucket}/${data.key}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `${target} backup failed`);
    } finally {
      setBackingUp(null);
    }
  };

  const scalewayReady = !!(settings?.scaleway?.access_key && settings?.scaleway?.bucket && settings?.scaleway?.secret_key_set);
  const nasReady = !!(settings?.nas?.endpoint_url && settings?.nas?.access_key && settings?.nas?.bucket && settings?.nas?.secret_key_set);

  const filteredSales = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sales.filter((s) => {
      if (customerTypeFilter !== "all" && s.customer_type !== customerTypeFilter) return false;
      if (!q) return true;
      return (
        s.customer_name?.toLowerCase().includes(q) ||
        s.grass_variety?.toLowerCase().includes(q) ||
        s.custom_variety?.toLowerCase().includes(q) ||
        s.date?.includes(q)
      );
    });
  }, [sales, search, customerTypeFilter]);

  const filteredExpenses = useMemo(() => {
    const q = search.trim().toLowerCase();
    return expenses.filter((x) => {
      if (categoryFilter !== "all" && x.category !== categoryFilter) return false;
      if (!q) return true;
      return (
        x.category?.toLowerCase().includes(q) ||
        x.notes?.toLowerCase().includes(q) ||
        x.date?.includes(q)
      );
    });
  }, [expenses, search, categoryFilter]);

  return (
    <div className="space-y-6">
      <GrainStockWidget
        settings={settings}
        isMaster={isMaster}
        isPreProduction={isPreProduction}
        onReset={resetInventoryToZero}
      />

      {/* Page header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Tab 2</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900">Logs</h1>
          <p className="text-sm text-slate-500 mt-1">Expenses · Sales summary · History</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={doExport}
            disabled={exporting || (sales.length === 0 && expenses.length === 0)}
            className="bg-slate-800 hover:bg-slate-900 text-white"
            data-testid="export-excel-button"
          >
            <ShareNetwork size={18} weight="duotone" className="mr-1.5" />
            {exporting ? "Exporting…" : "Export Excel"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                disabled={packBuilding || (sales.length === 0 && receipts.length === 0)}
                className="bg-indigo-700 hover:bg-indigo-800 text-white"
                data-testid="filing-pack-button"
                title="Quarterly Moms Filing pack (Skatteverket-ready zip)"
              >
                <Archive size={18} weight="duotone" className="mr-1.5" />
                {packBuilding ? (packStatus || "Building…") : "Filing Pack"}
                <CaretDown size={14} weight="bold" className="ml-1.5 opacity-80" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 p-3" data-testid="filing-pack-menu">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-slate-500 p-0 mb-2">
                Quarterly Moms Pack
              </DropdownMenuLabel>
              <p className="text-xs text-slate-600 mb-3 leading-snug">
                Bundles ledger Excel + all receipt &amp; credit-note PDFs for the chosen quarter into one .zip — Skatteverket-ready.
              </p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Year</Label>
                  <Select value={String(packYear)} onValueChange={(v) => setPackYear(+v)}>
                    <SelectTrigger className="h-9" data-testid="pack-year-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {yearOptions.map((y) => (
                        <SelectItem key={y} value={String(y)} data-testid={`pack-year-${y}`}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Quarter</Label>
                  <Select value={String(packQuarter)} onValueChange={(v) => setPackQuarter(+v)}>
                    <SelectTrigger className="h-9" data-testid="pack-quarter-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4].map((q) => {
                        const [s, e] = quarterRange(packYear, q);
                        return (
                          <SelectItem key={q} value={String(q)} data-testid={`pack-q-${q}`}>
                            Q{q} · {s.slice(5)} → {e.slice(5)}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                onClick={buildQuarterlyPack}
                disabled={packBuilding}
                className="w-full bg-indigo-700 hover:bg-indigo-800 text-white"
                data-testid="filing-pack-build"
              >
                <Archive size={16} weight="duotone" className="mr-1.5" />
                {packBuilding ? (packStatus || "Building…") : `Build ${packYear} Q${packQuarter} pack`}
              </Button>
              <p className="text-[10px] text-slate-400 mt-2 text-center">7-year retention · BFL 5 kap.</p>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                disabled={!!backingUp || (sales.length === 0 && expenses.length === 0)}
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                data-testid="backup-now-button"
              >
                <CloudArrowUp size={18} weight="duotone" className="mr-1.5" />
                {backingUp ? "Backing up…" : "Backup Now"}
                <CaretDown size={14} weight="bold" className="ml-1.5 opacity-80" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64" data-testid="backup-menu">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Choose destination
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={backupToICloud} className="gap-2 cursor-pointer" data-testid="backup-icloud-option">
                <AppleLogo size={18} weight="fill" className="text-slate-800" />
                <div className="flex flex-col">
                  <span className="font-medium">iCloud Drive</span>
                  <span className="text-[11px] text-slate-500">Save to Files via Share Sheet</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => backupToCloud("scaleway")} disabled={!scalewayReady} className="gap-2 cursor-pointer" data-testid="backup-scaleway-option">
                <CloudArrowUp size={18} weight="duotone" className="text-blue-700" />
                <div className="flex flex-col">
                  <span className="font-medium">Scaleway S3 {!scalewayReady && <span className="text-[10px] font-normal text-slate-400">(not configured)</span>}</span>
                  <span className="text-[11px] text-slate-500">{scalewayReady ? settings?.scaleway?.bucket : "Configure in Settings"}</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => backupToCloud("nas")} disabled={!nasReady} className="gap-2 cursor-pointer" data-testid="backup-nas-option">
                <CloudArrowUp size={18} weight="duotone" className="text-emerald-700" />
                <div className="flex flex-col">
                  <span className="font-medium">{settings?.nas?.label || "Personal NAS"} {!nasReady && <span className="text-[10px] font-normal text-slate-400">(not configured)</span>}</span>
                  <span className="text-[11px] text-slate-500">
                    {nasReady ? `${settings?.nas?.bucket} @ ${settings?.nas?.endpoint_url}` : "Configure in Settings"}
                  </span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* TOP: NEW EXPENSE form */}
      <Card data-testid="expense-form-card" className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <ReceiptIcon size={20} weight="duotone" className="text-slate-700" />
            New Expense
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form className="grid grid-cols-2 gap-3" onSubmit={submitExpense} data-testid="expense-form">
            <div>
              <Label className="text-slate-700">Date</Label>
              <Input
                type="date"
                value={expenseForm.date}
                onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                required
                data-testid="expense-date-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-700 flex items-center gap-1.5">
                Amount (SEK)
                {autoField === "amount_sek" && (
                  <span className="text-[9px] uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full font-bold">Auto</span>
                )}
              </Label>
              <Input
                type="number" step="0.01" min="0"
                value={expenseForm.amount_sek}
                onChange={(e) => handleExpenseField("amount_sek", e.target.value)}
                required
                data-testid="expense-amount-input"
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-slate-700">Category</Label>
              <Select
                value={expenseForm.category}
                onValueChange={(v) => {
                  editHistoryRef.current = [];
                  setExpenseForm({ ...expenseForm, category: v });
                }}
              >
                <SelectTrigger className="mt-1" data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-slate-700 flex items-center gap-1.5">
                Input VAT rate
                <span className="text-[10px] text-slate-500 font-normal">(amount above is GROSS · incl. VAT)</span>
              </Label>
              <Select
                value={expenseForm.vat_rate}
                onValueChange={(v) => setExpenseForm({ ...expenseForm, vat_rate: v })}
              >
                <SelectTrigger className="mt-1" data-testid="expense-vat-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.25" data-testid="vat-25">25% — Standard (most goods &amp; services)</SelectItem>
                  <SelectItem value="0.12" data-testid="vat-12">12% — Restaurants, hotels</SelectItem>
                  <SelectItem value="0.06" data-testid="vat-6">6% — Food, books, transport</SelectItem>
                  <SelectItem value="0" data-testid="vat-0">0% — Exempt / EU intra-community</SelectItem>
                </SelectContent>
              </Select>
              {parseFloat(expenseForm.amount_sek) > 0 && (
                <p className="text-[11px] text-slate-500 mt-1" data-testid="expense-net-hint">
                  Net (excl. VAT): <span className="font-mono text-slate-800">
                    {(parseFloat(expenseForm.amount_sek) / (1 + parseFloat(expenseForm.vat_rate || "0"))).toFixed(2)} SEK
                  </span>
                  · Reclaimable input Moms: <span className="font-mono text-blue-700">
                    {(parseFloat(expenseForm.amount_sek) - parseFloat(expenseForm.amount_sek) / (1 + parseFloat(expenseForm.vat_rate || "0"))).toFixed(2)} SEK
                  </span>
                </p>
              )}
            </div>
            <div className="col-span-2">
              <Label className="text-slate-700">Notes (optional)</Label>
              <Textarea
                rows={2}
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                data-testid="expense-notes-input"
                className="mt-1"
              />
            </div>

            {expenseForm.category === "Grains/Seeds" && (
              <>
                <div className="col-span-2 mt-1 pt-3 border-t border-slate-200">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-800 font-bold">Grain Purchase Details</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Enter any 2 of <span className="font-semibold">Amount · Quantity · Price/kg</span> — the third fills automatically</div>
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-700">Grain Variety</Label>
                  <Select value={expenseForm.grain_variety} onValueChange={(v) => setExpenseForm({ ...expenseForm, grain_variety: v })}>
                    <SelectTrigger className="mt-1" data-testid="expense-grain-variety-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GRASS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-slate-700">Supplier Name</Label>
                  <Input
                    value={expenseForm.supplier_name}
                    onChange={(e) => setExpenseForm({ ...expenseForm, supplier_name: e.target.value })}
                    placeholder="Lantmännen AB"
                    data-testid="expense-supplier-input"
                    className="mt-1"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-slate-700">Batch Number</Label>
                  <Input
                    value={expenseForm.batch_number}
                    onChange={(e) => setExpenseForm({ ...expenseForm, batch_number: e.target.value })}
                    placeholder="LB-2026-013"
                    data-testid="expense-batch-input"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 flex items-center gap-1.5">
                    Quantity (kg)
                    {autoField === "quantity_kg" && (
                      <span className="text-[9px] uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full font-bold">Auto</span>
                    )}
                  </Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={expenseForm.quantity_kg}
                    onChange={(e) => handleExpenseField("quantity_kg", e.target.value)}
                    data-testid="expense-quantity-kg-input"
                    className="mt-1"
                    placeholder="25"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 flex items-center gap-1.5">
                    Price / kg (SEK)
                    {autoField === "price_per_kg" && (
                      <span className="text-[9px] uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full font-bold">Auto</span>
                    )}
                  </Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={expenseForm.price_per_kg}
                    onChange={(e) => handleExpenseField("price_per_kg", e.target.value)}
                    data-testid="expense-price-per-kg-input"
                    className="mt-1"
                    placeholder="18.50"
                  />
                </div>
                {(() => {
                  const kg = parseFloat(expenseForm.quantity_kg);
                  const pkg = parseFloat(expenseForm.price_per_kg);
                  const amt = parseFloat(expenseForm.amount_sek);
                  if (!isNaN(kg) && !isNaN(pkg) && kg > 0 && pkg > 0) {
                    const calcAmt = !isNaN(amt) && amt > 0 ? amt : +(kg * pkg).toFixed(2);
                    return (
                      <div className="col-span-2 -mt-1">
                        <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900 flex items-center justify-between flex-wrap gap-1">
                          <span>{kg} kg × {pkg} SEK/kg</span>
                          <span className="font-heading font-bold text-emerald-800">= {formatSEK(calcAmt)}</span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </>
            )}
            <div className="col-span-2">
              <Button type="submit" disabled={savingExpense} className="w-full bg-slate-700 hover:bg-slate-800 text-white" data-testid="expense-submit-button">
                <Plus size={18} weight="bold" className="mr-1.5" />
                {savingExpense ? "Saving…" : "Add Expense"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* MIDDLE: SALES LOG DATA — aggregated from Receipt tab */}
      <SalesLogData sales={sales} />

      {/* BOTTOM: HISTORY */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="font-heading text-lg">History</CardTitle>
              <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                {historyTab === "sales" ? filteredSales.length : filteredExpenses.length} records
              </Badge>
            </div>
            <div className="relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by customer, variety, date, notes…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="history-search-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={historyTab} onValueChange={setHistoryTab}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
              <TabsList>
                <TabsTrigger value="sales" data-testid="history-tab-sales">Sales ({sales.length})</TabsTrigger>
                <TabsTrigger value="expenses" data-testid="history-tab-expenses">Expenses ({expenses.length})</TabsTrigger>
              </TabsList>
              <div className="flex items-center gap-2 text-sm">
                <Funnel size={16} className="text-slate-500" />
                {historyTab === "sales" ? (
                  <Select value={customerTypeFilter} onValueChange={setCustomerTypeFilter}>
                    <SelectTrigger className="w-[180px] h-9" data-testid="history-filter-customer-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All customer types</SelectItem>
                      {CUSTOMER_TYPE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-[180px] h-9" data-testid="history-filter-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All categories</SelectItem>
                      {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <TabsContent value="sales">
              {filteredSales.length === 0 ? (
                <EmptyState label="No sales yet — create one from the Receipt tab" />
              ) : (
                <ul className="divide-y divide-slate-200" data-testid="sales-history-list">
                  {filteredSales.map((s) => (
                    <li key={s.id} className={`py-3 flex items-start justify-between gap-3 ${s.voided ? "opacity-80" : ""}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-medium truncate ${s.voided ? "text-slate-500 line-through" : "text-slate-900"}`}>{s.customer_name}</span>
                          <Badge variant="outline" className={
                            s.customer_type === "Human Food"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-amber-50 text-amber-700 border-amber-200"
                          }>
                            {s.customer_type} · {s.customer_type === "Human Food" ? "6%" : "25%"}
                          </Badge>
                          {s.voided && (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 font-bold" data-testid={`sale-voided-${s.id}`}>
                              VOIDED
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {s.date} · {s.grass_variety === "Custom" ? `Custom: ${s.custom_variety}` : s.grass_variety} · {s.quantity_trays} trays · {(+s.total_weight_lbs * LB_TO_KG).toFixed(2)} kg
                        </div>
                        {s.voided && (
                          <div className="text-[11px] text-red-700 mt-1">
                            Cancelled · {({incorrect_entry: "Incorrect entry", customer_cancelled: "Customer cancelled", other: "Other"})[s.void_reason] || s.void_reason}
                            {s.void_reason === "other" && s.void_reason_text ? `: "${s.void_reason_text}"` : ""}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`font-heading font-semibold ${s.voided ? "text-slate-400 line-through" : "text-blue-700"}`}>{formatSEK(s.net_price_sek)}</div>
                        {s.voided && (
                          <div className="text-xs font-bold text-red-700">Effective: {formatSEK(0)}</div>
                        )}
                        <button
                          onClick={() => removeSale(s.id)}
                          className="text-slate-400 hover:text-red-600 mt-1 inline-flex items-center gap-1 text-xs"
                          data-testid={`delete-sale-${s.id}`}
                        >
                          <Trash size={14} /> Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>

            <TabsContent value="expenses">
              {filteredExpenses.length === 0 ? (
                <EmptyState label="No expenses found" />
              ) : (
                <ul className="divide-y divide-slate-200" data-testid="expenses-history-list">
                  {filteredExpenses.map((x) => {
                    const isGrain = x.category === "Grains/Seeds";
                    const kg = +x.quantity_kg || 0;
                    const amt = +x.amount_sek || 0;
                    const pkgStored = +x.price_per_kg || 0;
                    // Derive missing per-kg from total amount ÷ kg
                    const pkgComputed = isGrain && kg > 0 && amt > 0 ? +(amt / kg).toFixed(2) : 0;
                    const pkg = pkgStored || pkgComputed;
                    const pkgIsDerived = isGrain && !pkgStored && pkgComputed > 0;
                    return (
                    <li key={x.id} className="py-3 flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900">{x.category}</span>
                          {isGrain && kg > 0 ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">
                              +{kg} kg
                            </Badge>
                          ) : null}
                          {isGrain && pkg > 0 ? (
                            <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200" data-testid={`expense-per-kg-${x.id}`}>
                              {pkg} SEK/kg{pkgIsDerived ? " · auto" : ""}
                            </Badge>
                          ) : null}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {x.date}
                          {x.grain_variety ? ` · ${x.grain_variety}` : ""}
                          {x.supplier_name ? ` · ${x.supplier_name}` : ""}
                          {x.batch_number ? ` · Batch ${x.batch_number}` : ""}
                          {isGrain && kg > 0 && pkg > 0 ? ` · ${kg} kg × ${pkg} SEK/kg` : ""}
                          {x.notes ? ` · ${x.notes}` : ""}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-heading font-semibold text-red-700" data-testid={`expense-total-${x.id}`}>{formatSEK(amt)}</div>
                        {isGrain && pkg > 0 && (
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {pkg} SEK/kg
                          </div>
                        )}
                        <button
                          onClick={() => removeExpense(x.id)}
                          className="text-slate-400 hover:text-red-600 mt-1 inline-flex items-center gap-1 text-xs"
                          data-testid={`delete-expense-${x.id}`}
                        >
                          <Trash size={14} /> Delete
                        </button>
                      </div>
                    </li>
                  );})}
                </ul>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   SALES LOG DATA — aggregated overview, sourced from sales feed
   (sales are auto-created by the Receipt tab)
   ──────────────────────────────────────────────────────────────── */
function SalesLogData({ sales }) {
  const stats = useMemo(() => {
    const today = todayISO();
    const monthPrefix = today.slice(0, 7); // YYYY-MM
    const yearPrefix = today.slice(0, 4);  // YYYY

    const empty = { count: 0, trays: 0, kg: 0, sek: 0 };
    const out = {
      total: { ...empty },
      today: { ...empty },
      month: { ...empty },
      year: { ...empty },
      byVariety: {},
    };

    sales.forEach((s) => {
      const trays = +s.quantity_trays || 0;
      const kg = (+s.total_weight_lbs || 0) * LB_TO_KG;
      const sek = +s.net_price_sek || 0;
      const variety = s.grass_variety === "Custom" ? (s.custom_variety || "Custom") : (s.grass_variety || "—");

      const bump = (b) => { b.count += 1; b.trays += trays; b.kg += kg; b.sek += sek; };

      bump(out.total);
      if (s.date === today) bump(out.today);
      if (s.date?.startsWith(monthPrefix)) bump(out.month);
      if (s.date?.startsWith(yearPrefix)) bump(out.year);
      if (!out.byVariety[variety]) out.byVariety[variety] = { ...empty };
      bump(out.byVariety[variety]);
    });
    return out;
  }, [sales]);

  const monthLabel = new Date().toLocaleDateString(undefined, { month: "long" });
  const yearLabel = new Date().getFullYear();

  return (
    <Card className="border-slate-200" data-testid="sales-log-data-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <ChartLineUp size={20} weight="duotone" className="text-emerald-800" />
            Sales Log Data
          </CardTitle>
          <span className="text-[11px] text-slate-500">Sourced from the Receipt tab</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Hero — total sales */}
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 md:p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-emerald-700 flex items-center justify-center shrink-0">
              <CurrencyCircleDollar size={26} weight="duotone" color="white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-800">Total Sales Made</div>
              <div className="flex items-baseline gap-2 mt-0.5 flex-wrap">
                <span className="font-heading text-3xl md:text-4xl font-extrabold text-emerald-900" data-testid="sales-total-count">
                  {stats.total.count}
                </span>
                <span className="text-xs text-slate-500">receipts</span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-600 mt-1">
                <span><span className="font-semibold text-emerald-900" data-testid="sales-total-sek">{formatSEK(stats.total.sek)}</span> revenue</span>
                <span><span className="font-semibold text-emerald-900" data-testid="sales-total-kg">{stats.total.kg.toFixed(2)} kg</span> harvested</span>
                <span><span className="font-semibold text-emerald-900" data-testid="sales-total-trays">{stats.total.trays.toFixed(1)}</span> trays</span>
              </div>
            </div>
          </div>
        </div>

        {/* Three time-period cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <PeriodCard label="Today" sub={todayISO()} stats={stats.today} testId="sales-period-today" />
          <PeriodCard label={monthLabel} sub={`This month · ${yearLabel}`} stats={stats.month} testId="sales-period-month" />
          <PeriodCard label={String(yearLabel)} sub="This year" stats={stats.year} testId="sales-period-year" />
        </div>

        {/* Per-variety breakdown */}
        {Object.keys(stats.byVariety).length > 0 && (
          <div className="pt-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">By Variety</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {Object.entries(stats.byVariety)
                .sort((a, b) => b[1].sek - a[1].sek)
                .map(([variety, v]) => (
                  <div
                    key={variety}
                    className="rounded-lg border border-slate-200 bg-white p-3"
                    data-testid={`sales-variety-${variety.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-600 truncate">{variety}</div>
                    <div className="font-heading text-lg font-bold text-emerald-900 mt-0.5">
                      {v.kg.toFixed(2)} <span className="text-xs font-medium text-slate-500">kg</span>
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {v.count} sale{v.count !== 1 ? "s" : ""} · {formatSEK(v.sek)}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {stats.total.count === 0 && (
          <div className="text-center text-sm text-slate-500 py-2" data-testid="sales-log-empty">
            No sales yet — issue a receipt from the Receipt tab and it will appear here.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const PeriodCard = ({ label, sub, stats, testId }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={testId}>
    <div className="flex items-center gap-1.5">
      <Calendar size={13} weight="duotone" className="text-slate-500" />
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-600">{label}</div>
    </div>
    <div className="font-heading text-2xl font-extrabold text-blue-800 mt-1">{stats.count}</div>
    <div className="text-[10px] text-slate-500 mb-1">{sub}</div>
    <div className="text-[11px] text-slate-700 flex flex-wrap gap-x-3">
      <span><span className="font-semibold">{formatSEK(stats.sek)}</span></span>
      <span><span className="font-semibold">{stats.kg.toFixed(2)} kg</span></span>
    </div>
  </div>
);

const EmptyState = ({ label }) => (
  <div className="text-center py-12 text-slate-500 text-sm" data-testid="empty-state">{label}</div>
);

const GrainStockWidget = ({ settings, isMaster, isPreProduction, onReset }) => {
  const ratio = settings?.seed_per_tray_ratio ?? 0.25;
  const stocks = settings?.current_grain_stock || {};
  const VARIETIES = ["Wheat", "Barley", "Corn"];

  const toneFor = (stock) => {
    if (stock < 15) return {
      box: "bg-red-50 border-red-300 ring-2 ring-red-200 animate-grain-flash",
      number: "text-red-700", label: "text-red-700",
      pill: "bg-red-700 text-white",
      status: "Critical", Icon: Warning,
    };
    if (stock < 50) return {
      box: "bg-orange-50 border-orange-300",
      number: "text-orange-700", label: "text-orange-700",
      pill: "bg-orange-600 text-white",
      status: "Low", Icon: Warning,
    };
    return {
      box: "bg-emerald-50 border-emerald-200",
      number: "text-emerald-800", label: "text-emerald-800",
      pill: "bg-emerald-700 text-white",
      status: "Healthy", Icon: Package,
    };
  };

  const totalStock = VARIETIES.reduce((a, v) => a + (stocks[v] || 0), 0);
  const totalTone = toneFor(totalStock);

  return (
    <div data-testid="grain-stock-widget" className="space-y-3">
      <div className={`relative rounded-xl border ${totalTone.box} p-4 md:p-5 transition-all`}>
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-lg ${totalTone.pill} flex items-center justify-center shrink-0`}>
            <totalTone.Icon size={26} weight="duotone" color="white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${totalTone.label}`}>Total Grain Stock</div>
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className={`font-heading text-3xl md:text-4xl font-extrabold ${totalTone.number}`} data-testid="grain-stock-value">
                {totalStock.toFixed(2)} kg
              </span>
              <span className="text-xs text-slate-500">@ {ratio} kg/tray</span>
            </div>
            <div className={`text-xs font-medium mt-1 ${totalTone.label}`} data-testid="grain-stock-status">{totalTone.status}</div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {VARIETIES.map((v) => {
          const stock = stocks[v] || 0;
          const tone = toneFor(stock);
          return (
            <div key={v} className={`rounded-lg border ${tone.box} p-3`} data-testid={`grain-stock-${v.replace(' ', '-').toLowerCase()}`}>
              <div className={`text-[9px] uppercase tracking-[0.15em] font-bold ${tone.label} truncate`}>{v}</div>
              <div className={`font-heading text-lg sm:text-xl font-bold mt-0.5 ${tone.number}`}>{stock.toFixed(1)} kg</div>
              <div className={`text-[10px] ${tone.label}`}>{tone.status}</div>
            </div>
          );
        })}
      </div>
      {/* Master-only, pre-production-only: zero out the inventory.
          Disappears the moment "Mark today as start of production" is clicked. */}
      {isMaster && isPreProduction && (
        <button
          type="button"
          onClick={onReset}
          className="w-full rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 text-xs font-semibold py-2 transition-colors"
          data-testid="reset-inventory-zero-button"
          title="Pre-production utility — sets Wheat / Barley / Corn stocks to 0 kg"
        >
          Reset Inventory to Zero
          <span className="ml-2 text-[10px] font-normal text-amber-700">(pre-production only)</span>
        </button>
      )}
    </div>
  );
};
